import { clone } from '@jack-henry/frontend-utils/functions';
import {
    DayOfWeekDto as BoDayOfWeekDto,
    TimeAccessTypeDto as BoTimeAccessTypeDto,
    UserTimeAccessDayDto as BoUserTimeAccessDayDto,
} from '@treasury/api/bo';
import {
    DayOfWeekDto as ConsumerDayOfWeekDto,
    TimeAccessTypeDto as ConsumerTimeAccessTypeDto,
    UserTimeAccessDayDto as ConsumerUserTimeAccessDayDto,
} from '@treasury/api/channel';
import { DomainEntity } from '../../../../shared';

const nullCompanyUserTimeAccessDay: BoUserTimeAccessDayDto = {
    timeAccessType: 0, // undefined
    dayOfWeek: 0, // Sunday
    beginTime: '',
    endTime: '',
};

export class UserTimeAccessDay extends DomainEntity<
    BoUserTimeAccessDayDto | ConsumerUserTimeAccessDayDto
> {
    constructor(dto = clone(nullCompanyUserTimeAccessDay)) {
        super(dto);
    }

    public createDefault(): this {
        return new UserTimeAccessDay(clone(nullCompanyUserTimeAccessDay)) as this;
    }

    public get timeAccessType() {
        return this.dto.timeAccessType as unknown as
            | BoTimeAccessTypeDto
            | ConsumerTimeAccessTypeDto;
    }

    public set timeAccessType(value: BoTimeAccessTypeDto | ConsumerTimeAccessTypeDto) {
        this.dto.timeAccessType = value as number;
    }

    public get dayOfWeek() {
        return this.dto.dayOfWeek as unknown as BoDayOfWeekDto | ConsumerDayOfWeekDto;
    }

    public get dayOfWeekName() {
        const dayOfWeekKey = Object.keys(BoDayOfWeekDto).find(
            key => BoDayOfWeekDto[key as keyof typeof BoDayOfWeekDto] === this.dayOfWeek
        );
        return dayOfWeekKey ?? 'Unknown';
    }

    public get beginTime() {
        if (!this.dto.beginTime) {
            return '';
        }
        return this.dto.beginTime;
    }

    public set beginTime(value: string) {
        this.dto.beginTime = /^\d{1,2}:\d{2}$/.test(value) ? `${value}:00` : value;
    }

    public get endTime() {
        if (!this.dto.endTime) {
            return '';
        }
        return this.dto.endTime;
    }

    public set endTime(value: string) {
        this.dto.endTime = /^\d{1,2}:\d{2}$/.test(value) ? `${value}:00` : value;
    }

    public get timeAccessLabel(): string {
        switch (this.dto.timeAccessType) {
            case BoTimeAccessTypeDto.AllowAllDay:
                return 'Allowed all day';
            case BoTimeAccessTypeDto.RestrictAllDay:
                return 'Restricted all day';
            case BoTimeAccessTypeDto.TimeRange:
                return `${this.formatTimeAmPm(this.beginTime)} – ${this.formatTimeAmPm(this.endTime)}`;
            default:
                return 'Unrestricted';
        }
    }

    private formatTimeAmPm(time: string): string {
        const [hoursStr, minutesStr] = time.split(':');
        const hours = parseInt(hoursStr, 10);
        if (isNaN(hours)) {
            return time;
        }
        const minutes = minutesStr ?? '00';
        const period = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        return `${displayHours}:${minutes} ${period}`;
    }
}
